<template>
  <div class="folder-dropdown-new">
    <div v-if="folders.length" class="folder-dropdown-new__header">Move to</div>
    <div
      v-for="folder in folders"
      :key="folder.id"
      class="folder-dropdown-new__item"
      @click="onSelectItem(folder)"
    >
      <FontAwesomeIcon
        :icon="['fak', 'folder']"
        class="text-neutral-accent h-16 w-16"
      />
      <div class="folder-dropdown-new__item-name">
        {{ folder.name }}
      </div>
    </div>
    <BittsDivider v-if="folders.length" class="my-2" />
    <div
      v-if="moveToUnfiled"
      class="folder-dropdown-new__item"
      @click="onSelectItem(MOVE_TO_UNFILLED)"
    >
      <FontAwesomeIcon
        :icon="['fak', 'move']"
        class="text-neutral-accent h-16 w-16"
      />
      <div class="folder-dropdown-new__item-name">Move to Unfiled</div>
    </div>
    <div
      v-else
      class="folder-dropdown-new__item"
      @click="onSelectItem(NEW_FOLDER)"
    >
      <FontAwesomeIcon
        :icon="['fas', 'folder-plus']"
        class="text-neutral-accent h-16 w-16"
      />
      <div class="folder-dropdown-new__item-name">New Folder</div>
    </div>
    <component
      :is="canShareAll ? 'div' : BittsTooltip"
      v-if="canShare"
      v-bind="canShareAll ? {} : { mountToBody: true }"
    >
      <div
        class="folder-dropdown-new__item"
        :class="{ 'folder-dropdown-new__item--disabled': !canShareAll }"
        @click="canShareAll && emit('share-reports')"
      >
        <FontAwesomeIcon
          :icon="['fak', 'share']"
          class="text-neutral-accent h-16 w-16"
        />
        <div class="folder-dropdown-new__item-name">Share</div>
      </div>
      <template v-if="!canShareAll" #title>
        You don't have permission to share some or all of the selected Lists
      </template>
    </component>
    <component
      :is="canDeleteAll ? 'div' : BittsTooltip"
      v-if="!hasHiddenPartners"
      v-bind="canDeleteAll ? {} : { mountToBody: true }"
    >
      <div
        class="folder-dropdown-new__item"
        :class="{ 'folder-dropdown-new__item--disabled': !canDeleteAll }"
        @click="canDeleteAll && deleteReports()"
      >
        <FontAwesomeIcon
          :icon="['fak', 'delete']"
          class="text-danger-accent h-16 w-16"
        />
        <div class="folder-dropdown-new__item-name text-danger-text">
          Delete Lists
        </div>
      </div>
      <template v-if="!canDeleteAll" #title>
        You don't have permission to delete some or all of the selected Lists
      </template>
    </component>
  </div>
</template>

<script setup lang="ts">
import { BittsDivider, BittsTooltip } from '@crossbeam/bitts';

import { storeToRefs } from 'pinia';

import { useRootStore } from '@/stores/RootStore';
import { ReportFolder } from '@/types/reports';

const {
  folders = [],
  moveToUnfiled = false,
  canShare = false,
  canShareAll = true,
  canDeleteAll = true,
} = defineProps<{
  folders: ReportFolder[];
  moveToUnfiled?: boolean;
  canShare?: boolean;
  canShareAll?: boolean;
  canDeleteAll?: boolean;
}>();

const emit = defineEmits<{
  (e: 'item-selected', item: ReportFolder): void;
  (e: 'delete-reports' | 'share-reports'): void;
}>();

const { hasHiddenPartners } = storeToRefs(useRootStore());

const NEW_FOLDER: ReportFolder = {
  name: 'New Folder',
  id: 'new-folder',
  reports: [],
  updated_at: '',
  created_at: '',
};
const MOVE_TO_UNFILLED: ReportFolder = {
  name: 'Move To Unfiled',
  id: 'move-to-unfiled',
  reports: [],
  updated_at: '',
  created_at: '',
};
function onSelectItem(item: ReportFolder) {
  emit('item-selected', item);
}

function deleteReports() {
  emit('delete-reports');
}
</script>

<style scoped lang="pcss">
@reference "../../css/theme.css";

.folder-dropdown-new {
  @apply w-[200px] flex flex-col gap-2;
}

.folder-dropdown-new__header {
  @apply text-sm text-neutral-text px-4;
}

.folder-dropdown-new__item {
  @apply py-4 px-8 flex items-center gap-8 rounded mx-4;

  &:hover {
    @apply bg-neutral-background-weak cursor-pointer;
  }
}

.folder-dropdown-new__item-name {
  @apply text-neutral-text-strong text-base;
}

.folder-dropdown-new__item--disabled {
  @apply cursor-not-allowed opacity-50;

  &:hover {
    @apply bg-transparent cursor-not-allowed;
  }
}
</style>
